/**
 * View component for the intake form screen.
 *
 * Shows an error state, loading skeleton, or the Form.io form
 * in an iframe (web) or WebView (native). When voice dictation
 * is enabled, renders the DictateButton as an absolute-positioned
 * overlay, plus RecordingBar and ProcessingOverlay around the form.
 * @module features/player-intake/screens/Main/MainView
 */

import Constants from "expo-constants";
import { memo } from "react";
import { Platform } from "react-native";
import { WebView, WebViewMessageEvent } from "react-native-webview";

import FormLoadingSkeleton from "@/components/molecules/FormLoadingSkeleton";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { ProcessingOverlay, RecordingBar } from "@/features/voice-report";
import type { DictationStatus } from "@/features/voice-report";

import { INJECTED_JS } from "../../utils/formBridgeScript";

/**
 * Native WebView fill style. `flex: 1` plus the status-bar top offset are
 * layout values with no closed className surface on the raw RN WebView
 * (owned by the import-closure wave); hoisted to a module constant so it
 * reaches the component via a single-brace style prop rather than an inline
 * object literal. `Constants.statusBarHeight` is a stable module value, so
 * the result is byte-for-byte identical to the prior inline style.
 */
const WEBVIEW_STYLE = {
  flex: 1,
  marginTop: Constants.statusBarHeight,
} as const;

/**
 * Props for the MainView component
 */
interface MainViewProps {
  /** The URL to display in the WebView or iframe */
  readonly url: string;
  /** Whether the form URL is still loading from the server */
  readonly loading: boolean;
  /** Whether the form URL query returned an error */
  readonly hasError: boolean;
  /** Whether dark theme is active for skeleton styling */
  readonly isDark: boolean;
  /** Current dictation status */
  readonly dictationStatus: DictationStatus;
  /** Elapsed recording time */
  readonly elapsedSeconds: number;
  /** Live transcript from speech recognition */
  readonly liveTranscript: string;
  /** Real-time frequency levels from Web Audio API (0-1 range) */
  readonly audioLevels: readonly number[];
  /** Processing status message */
  readonly processingMessage: string;
  /** Cancel recording */
  readonly onCancelRecording: () => void;
  /** Finish recording */
  readonly onFinishRecording: () => void;
  /** Ref for the web iframe element (used for postMessage injection) */
  readonly iframeRef?: React.RefObject<HTMLIFrameElement | null>;
  /** Callback when WebView receives a message from injected JS */
  readonly onWebViewMessage?: (event: WebViewMessageEvent) => void;
  /** Callback when WebView navigation state changes */
  readonly onWebViewNavigationChange?: (navState: {
    url?: string;
    loading?: boolean;
    title?: string;
  }) => void;
  /** Callback when WebView encounters a loading error */
  readonly onWebViewError?: (syntheticEvent: {
    nativeEvent: { description?: string };
  }) => void;
  /** Callback when WebView finishes loading */
  readonly onWebViewLoadEnd?: () => void;
  /** Ref for the native WebView element (used for injectJavaScript on native) */
  readonly webViewRef?: React.RefObject<WebView | null>;
}

/**
 * Intake form view with optional voice dictation UI.
 * @param root0 - Component props
 * @param root0.url - Form URL
 * @param root0.loading - Loading state
 * @param root0.hasError - Error state
 * @param root0.isDark - Dark mode flag
 * @param root0.dictationStatus - Current dictation status
 * @param root0.elapsedSeconds - Recording timer
 * @param root0.liveTranscript - Live speech recognition transcript
 * @param root0.audioLevels - Real-time frequency levels for waveform
 * @param root0.processingMessage - Processing status text
 * @param root0.onCancelRecording - Cancel recording handler
 * @param root0.onFinishRecording - Finish recording handler
 * @param root0.iframeRef - Ref for web iframe element
 * @param root0.onWebViewMessage - WebView message handler
 * @param root0.onWebViewNavigationChange - WebView nav handler
 * @param root0.onWebViewError - WebView error handler
 * @param root0.onWebViewLoadEnd - WebView load end handler
 * @param root0.webViewRef - Ref for native WebView element
 */
const MainView = ({
  url,
  loading,
  hasError,
  isDark,
  dictationStatus,
  elapsedSeconds,
  liveTranscript,
  audioLevels,
  processingMessage,
  onCancelRecording,
  onFinishRecording,
  iframeRef,
  onWebViewMessage,
  onWebViewNavigationChange,
  onWebViewError,
  onWebViewLoadEnd,
  webViewRef,
}: MainViewProps) =>
  hasError ? (
    <Stack className="flex-1 items-center justify-center p-6">
      <Box className="gap-2 rounded-sm bg-status-error-surface p-6">
        <Text variant="title" className="text-center text-status-error">
          Unable to load intake form
        </Text>
        <Text variant="body" className="text-center text-status-error">
          The server failed to generate the form URL. Please go back and try
          again. If this persists, contact support.
        </Text>
      </Box>
    </Stack>
  ) : loading || !url ? (
    <FormLoadingSkeleton isDark={isDark} />
  ) : (
    <Box className="flex-1">
      {/* Form content — greyed out during recording and processing */}
      <Box
        className={`flex-1 ${dictationStatus === "recording" || dictationStatus === "processing" ? "opacity-40 pointer-events-none" : ""}`}
      >
        {Platform.OS === "web" ? (
          <iframe
            ref={iframeRef}
            src={url}
            title="Intake Form"
            className="h-full w-full"
          />
        ) : (
          <WebView
            ref={webViewRef}
            // eslint-disable-next-line no-restricted-syntax -- react-native-webview is a third-party native primitive with no atom; flex fill + runtime status-bar top offset
            style={WEBVIEW_STYLE}
            source={{ uri: url }}
            domStorageEnabled
            javaScriptEnabled
            originWhitelist={["https://*"]}
            injectedJavaScript={INJECTED_JS}
            onMessage={onWebViewMessage}
            onNavigationStateChange={onWebViewNavigationChange}
            onError={onWebViewError}
            onLoadEnd={onWebViewLoadEnd}
          />
        )}
      </Box>

      {/* Recording bar — sticky bottom */}
      {dictationStatus === "recording" && (
        <RecordingBar
          elapsedSeconds={elapsedSeconds}
          liveTranscript={liveTranscript}
          audioLevels={audioLevels}
          onCancel={onCancelRecording}
          onFinish={onFinishRecording}
        />
      )}

      {/* Processing overlay */}
      {dictationStatus === "processing" && (
        <ProcessingOverlay message={processingMessage} />
      )}
    </Box>
  );

MainView.displayName = "MainView";

export default memo(MainView);
